import { useCallback, useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { CreativesStatusEnum } from "@north-beam/nb-client-side";
import { SecondaryButton } from "@shared/buttons";

interface Props {
  onReset(): void;
  onApply(newStatuses: CreativesStatusEnum[] | null): void;
  onOpen(): void;
  onClose(): void;
  currentStatuses: CreativesStatusEnum[] | null;
  isOpen: boolean;
}

export const StatusFilterPopover = ({
  onReset,
  onApply,
  onOpen,
  onClose,
  currentStatuses,
  isOpen,
}: Props) => {
  const [proposedStatusFilters, setProposedStatusFilters] = useState<
    Set<CreativesStatusEnum>
  >(new Set(currentStatuses));

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (value) => (value ? onOpen() : onClose()),
    transform: false,
    placement: "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isOpen) {
      setProposedStatusFilters(new Set(currentStatuses));
    }
  }, [isOpen, currentStatuses, setProposedStatusFilters]);

  const onToggle = useCallback(
    (status: CreativesStatusEnum) => {
      const copy = new Set(proposedStatusFilters);
      if (proposedStatusFilters?.has(status)) {
        copy.delete(status);
        setProposedStatusFilters(copy);
      } else {
        copy.add(status);
        setProposedStatusFilters(copy);
      }
    },
    [proposedStatusFilters],
  );

  const onToggleAll = useCallback(() => {
    if (
      proposedStatusFilters.has(CreativesStatusEnum.active) &&
      proposedStatusFilters.has(CreativesStatusEnum.inactive)
    ) {
      setProposedStatusFilters(new Set());
    } else {
      setProposedStatusFilters(
        new Set([CreativesStatusEnum.active, CreativesStatusEnum.inactive]),
      );
    }
  }, [proposedStatusFilters]);

  const isAllSelected =
    proposedStatusFilters.has(CreativesStatusEnum.active) &&
    proposedStatusFilters.has(CreativesStatusEnum.inactive);

  return (
    <div>
      <button
        className={clsx(
          "flex justify-center items-center cursor-pointer",
          "rounded w-6 h-6 text-[0.65rem] ml-0.5",
          {
            "text-cosmic bg-cosmic-3": !isOpen,
            "text-white bg-primary": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <i className={clsx("fa-regular fa-sharp fa-filter")}></i>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            className={clsx(
              "absolute right-0 mt-2 px-4 pb-4 pt-4 w-52 z-[1000]",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <label className="flex items-center cursor-pointer">
              <input
                className="nb-input"
                type="checkbox"
                checked={isAllSelected}
                onChange={onToggleAll}
              />
              <span className="font-normal ml-1 px-2">
                {isAllSelected ? "Unselect All" : "Select All"}
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                className="nb-input"
                type="checkbox"
                checked={proposedStatusFilters.has(CreativesStatusEnum.active)}
                onChange={() => {
                  onToggle(CreativesStatusEnum.active);
                }}
              />
              <span className="text-[#3B8139] font-normal bg-alien-2 ml-2 px-2 py-0.5 rounded">
                Active
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                className="nb-input"
                checked={proposedStatusFilters.has(
                  CreativesStatusEnum.inactive,
                )}
                type="checkbox"
                onChange={() => onToggle(CreativesStatusEnum.inactive)}
              />
              <span className="font-normal bg-interstellar-3 ml-2 px-2 py-0.5 rounded">
                Inactive
              </span>
            </label>
            <div className="mt-5 flex gap-2">
              <SecondaryButton
                className="flex-1 font-semibold"
                onClick={() => {
                  onReset();
                  onClose();
                }}
              >
                {currentStatuses ? "RESET" : "CANCEL"}
              </SecondaryButton>
              <SecondaryButton
                className="flex-1 font-semibold"
                isDisabled={
                  (!currentStatuses || currentStatuses.length === 0) &&
                  proposedStatusFilters.size === 0
                }
                onClick={() => {
                  onApply(
                    proposedStatusFilters
                      ? Array.from(proposedStatusFilters)
                      : null,
                  );
                  onClose();
                }}
              >
                APPLY
              </SecondaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
